import React, { useEffect, useRef, useState } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

interface GridOverlayProps {
  gridSize: number; // 网格大小（度数）
  onGridClick: (gridX: number, gridY: number, lat: number, lng: number) => void;
  territories: Array<{ grid_x: number; grid_y: number; owner_id: string }>;
  currentUserId?: string;
}

const GridOverlay: React.FC<GridOverlayProps> = ({
  gridSize,
  onGridClick,
  territories,
  currentUserId
}) => {
  const map = useMap();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hoveredGrid, setHoveredGrid] = useState<{ x: number; y: number } | null>(null);

  // 将经纬度转换为网格坐标
  const latLngToGrid = (lat: number, lng: number) => ({
    gridX: Math.floor(lng / gridSize),
    gridY: Math.floor(lat / gridSize)
  });

  // 将网格坐标转换为经纬度
  const gridToLatLng = (gridX: number, gridY: number) => ({
    lat: gridY * gridSize,
    lng: gridX * gridSize
  });

  // 创建领土查找Map以提高性能
  const territoryMap = new Map<string, { owner_id: string }>();
  territories.forEach(t => {
    territoryMap.set(`${t.grid_x},${t.grid_y}`, { owner_id: t.owner_id });
  });

  // 处理地图事件
  useMapEvents({
    click: (e) => {
      const zoom = map.getZoom();
      const isMaxZoom = zoom >= 16;

      // 只在最大缩放级别时允许点击交互
      if (!isMaxZoom) {
        console.log('Grid interaction disabled at zoom level:', zoom);
        return;
      }

      const { lat, lng } = e.latlng;
      const { gridX, gridY } = latLngToGrid(lat, lng);

      // 计算网格中心点的经纬度
      const centerLat = (gridY + 0.5) * gridSize;
      const centerLng = (gridX + 0.5) * gridSize;

      console.log('Click event:', { lat, lng });
      console.log('Grid coords:', { gridX, gridY });
      console.log('Grid center:', { centerLat, centerLng });

      onGridClick(gridX, gridY, centerLat, centerLng);
    },

    mousemove: (e) => {
      const zoom = map.getZoom();
      const isMaxZoom = zoom >= 16;

      // 只在最大缩放级别时显示悬停效果
      if (!isMaxZoom) {
        if (hoveredGrid) {
          setHoveredGrid(null); // 清除悬停状态
        }
        return;
      }

      const { lat, lng } = e.latlng;
      const { gridX, gridY } = latLngToGrid(lat, lng);

      if (!hoveredGrid || hoveredGrid.x !== gridX || hoveredGrid.y !== gridY) {
        setHoveredGrid({ x: gridX, y: gridY });
      }
    },

    moveend: () => {
      requestAnimationFrame(redrawCanvas);
    },

    zoomend: () => {
      // 缩放结束时重置悬停状态
      setHoveredGrid(null);
      requestAnimationFrame(redrawCanvas);
    }
  });

  // 绘制网格
  const redrawCanvas = () => {
    if (!canvasRef.current || !map) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 获取地图边界和缩放级别
    const bounds = map.getBounds();
    const zoom = map.getZoom();

    // 设置canvas大小
    const size = map.getSize();
    canvas.width = size.x;
    canvas.height = size.y;
    canvas.style.width = size.x + 'px';
    canvas.style.height = size.y + 'px';

    // 清除画布
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();

    // 计算可视区域的网格范围
    const minGridX = Math.floor(southWest.lng / gridSize) - 1;
    const maxGridX = Math.ceil(northEast.lng / gridSize) + 1;
    const minGridY = Math.floor(southWest.lat / gridSize) - 1;
    const maxGridY = Math.ceil(northEast.lat / gridSize) + 1;

    // 始终绘制已占领的格子（所有缩放级别）
    for (let gridX = minGridX; gridX < maxGridX; gridX++) {
      for (let gridY = minGridY; gridY < maxGridY; gridY++) {
        const territory = territoryMap.get(`${gridX},${gridY}`);
        if (territory) {
          // 使用网格的精确边界
          const gridSouth = gridY * gridSize;
          const gridNorth = (gridY + 1) * gridSize;
          const gridWest = gridX * gridSize;
          const gridEast = (gridX + 1) * gridSize;

          const southWestCorner = map.latLngToContainerPoint([gridSouth, gridWest]);
          const northEastCorner = map.latLngToContainerPoint([gridNorth, gridEast]);

          // 根据所有者设置颜色
          const isOwner = territory.owner_id === currentUserId;
          ctx.fillStyle = isOwner ? 'rgba(76, 175, 80, 0.7)' : 'rgba(255, 87, 34, 0.7)';

          const width = northEastCorner.x - southWestCorner.x;
          const height = southWestCorner.y - northEastCorner.y;

          ctx.fillRect(southWestCorner.x, northEastCorner.y, width, height);

          // 添加边框让色块更明显
          ctx.strokeStyle = isOwner ? 'rgba(76, 175, 80, 0.9)' : 'rgba(255, 87, 34, 0.9)';
          ctx.lineWidth = Math.max(1, Math.min(3, width / 20)); // 根据格子大小调整边框粗细
          ctx.strokeRect(southWestCorner.x, northEastCorner.y, width, height);
        }
      }
    }

    // 只有在最大缩放级别(≥16)时才显示网格线和交互
    const isMaxZoom = zoom >= 16;

    if (isMaxZoom) {
      // 限制网格数量以保证性能（只在高缩放时需要考虑）
      const totalGrids = (maxGridX - minGridX) * (maxGridY - minGridY);
      const maxGrids = 30 * 30; // 最大缩放时适当减少格子数量

      if (totalGrids <= maxGrids) {
        // 绘制网格线
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.lineWidth = 1;

        // 绘制垂直线
        for (let gridX = minGridX; gridX <= maxGridX; gridX++) {
          const lng = gridX * gridSize;
          const southPoint = map.latLngToContainerPoint([southWest.lat, lng]);
          const northPoint = map.latLngToContainerPoint([northEast.lat, lng]);

          ctx.beginPath();
          ctx.moveTo(southPoint.x, southPoint.y);
          ctx.lineTo(northPoint.x, northPoint.y);
          ctx.stroke();
        }

        // 绘制水平线
        for (let gridY = minGridY; gridY <= maxGridY; gridY++) {
          const lat = gridY * gridSize;
          const westPoint = map.latLngToContainerPoint([lat, southWest.lng]);
          const eastPoint = map.latLngToContainerPoint([lat, northEast.lng]);

          ctx.beginPath();
          ctx.moveTo(westPoint.x, westPoint.y);
          ctx.lineTo(eastPoint.x, eastPoint.y);
          ctx.stroke();
        }

        // 绘制悬停的格子（只在网格模式下）
        if (hoveredGrid) {
          const territory = territoryMap.get(`${hoveredGrid.x},${hoveredGrid.y}`);
          if (!territory) { // 只在空白格子上显示悬停效果
            const gridSouth = hoveredGrid.y * gridSize;
            const gridNorth = (hoveredGrid.y + 1) * gridSize;
            const gridWest = hoveredGrid.x * gridSize;
            const gridEast = (hoveredGrid.x + 1) * gridSize;

            const southWestCorner = map.latLngToContainerPoint([gridSouth, gridWest]);
            const northEastCorner = map.latLngToContainerPoint([gridNorth, gridEast]);

            ctx.fillStyle = 'rgba(33, 150, 243, 0.4)';

            const width = northEastCorner.x - southWestCorner.x;
            const height = southWestCorner.y - northEastCorner.y;

            ctx.fillRect(southWestCorner.x, northEastCorner.y, width, height);

            // 绘制边框
            ctx.strokeStyle = 'rgba(33, 150, 243, 0.8)';
            ctx.lineWidth = 2;
            ctx.strokeRect(southWestCorner.x, northEastCorner.y, width, height);
          }
        }
      }
    }

    console.log(`Canvas redrawn - Zoom: ${zoom}, Grid mode: ${isMaxZoom}, Territories: ${territories.length}`);
  };

  useEffect(() => {
    if (!map || !canvasRef.current) return;

    const canvas = canvasRef.current;

    // 创建Canvas覆盖层
    const canvasElement = L.DomUtil.create('div', 'grid-overlay');
    canvasElement.style.pointerEvents = 'none';
    canvasElement.appendChild(canvas);

    // 添加到地图
    const pane = map.getPane('overlayPane');
    if (pane) {
      pane.appendChild(canvasElement);
    }

    // 初始绘制
    requestAnimationFrame(redrawCanvas);

    return () => {
      if (pane && canvasElement.parentNode === pane) {
        pane.removeChild(canvasElement);
      }
    };
  }, [map]);

  useEffect(() => {
    requestAnimationFrame(redrawCanvas);
  }, [territories, hoveredGrid, currentUserId]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 400
      }}
    />
  );
};

export default GridOverlay;